/**
 * React hook — whether an AI assist capability is enabled for the current workspace.
 *
 * A capability with an `elevationLevel` in the registry serves only pages
 * behind the Elevation gate, so it is enabled only for people at that level
 * (lib/elevationAccess.ts: 'owner' is Wes, 'team' is Wes and Harry), and for
 * nobody while it is not yet known who is signed in.
 */

import { useEffect } from 'react';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useAiAssistSettingsStore } from '../../stores/prioritization/aiAssistSettingsStore';
import { isAiCapabilityEnabled } from './aiAssistSettings';
import { hydrateWorkspacePlanCache } from './aiAssistSettings';
import { AI_ASSIST_CAPABILITIES, getAiCapability, type AiCapabilityMeta } from './aiCapabilityRegistry';
import { hasElevationLevel, useElevationAccess, type ElevationAccess } from '../elevationAccess';

/**
 * Whether someone at `access` may see and use a capability: one with an
 * Elevation level only at that level, every product capability for everyone.
 */
export function isAiCapabilityOpenTo(
  meta: AiCapabilityMeta | undefined,
  access: ElevationAccess | 'pending',
): boolean {
  const level = meta?.elevationLevel;
  if (!level) return true;
  return access !== 'pending' && hasElevationLevel(access, level);
}

/** The assists a person is shown in settings, and counted there. */
export function assistCapabilitiesFor(access: ElevationAccess | 'pending'): AiCapabilityMeta[] {
  return AI_ASSIST_CAPABILITIES.filter((c) => isAiCapabilityOpenTo(c, access));
}

export function useAiCapability(capabilityId: string): boolean {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const workspacePlan = useTenantStore((s) => s.workspacePlan);
  const version = useAiAssistSettingsStore((s) =>
    workspaceId ? s.byWorkspace[workspaceId]?.version ?? 0 : 0,
  );
  const fetchAll = useAiAssistSettingsStore((s) => s.fetchAll);
  const access = useElevationAccess();

  useEffect(() => {
    hydrateWorkspacePlanCache(workspacePlan);
  }, [workspacePlan]);

  useEffect(() => {
    if (workspaceId) void fetchAll(workspaceId);
  }, [workspaceId, fetchAll]);

  // version forces re-render after toggle save
  void version;

  if (!isAiCapabilityOpenTo(getAiCapability(capabilityId), access)) return false;
  return isAiCapabilityEnabled(capabilityId, workspaceId);
}
